import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface ReportModalProps {
  productoId: string;
  productoTitulo: string;
  onClose: () => void;
}

const RAZONES = [
  'Producto fraudulento o estafa',
  'Fotos falsas o engañosas',
  'Precio abusivo o engañoso',
  'Artículo prohibido',
  'Información de contacto falsa',
  'Duplicado o spam',
  'Otro',
];

export default function ReportModal({ productoId, productoTitulo, onClose }: ReportModalProps) {
  const { user } = useAuth();
  const [razon, setRazon] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!razon) {
      setError('Por favor selecciona una razón');
      return;
    }
    setEnviando(true);
    setError('');
    try {
      const { error: dbError } = await supabase.from('reports').insert({
        producto_id: productoId,
        reportado_por: user?.id || null,
        razon,
        descripcion: descripcion.trim() || null,
        estado: 'pendiente',
      });
      if (dbError) throw dbError;
      setEnviado(true);
    } catch (err) {
      setError('Error al enviar el reporte. Intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        {enviado ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-4xl text-emerald-500"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Reporte Enviado</h3>
            <p className="text-gray-500 text-sm mb-6">
              Gracias por ayudarnos a mantener IslaVende seguro. Revisaremos este anuncio a la brevedad.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-900 text-white rounded-xl font-medium cursor-pointer whitespace-nowrap"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl">
                  <i className="ri-flag-line text-red-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Reportar Anuncio</h3>
                  <p className="text-xs text-gray-500 line-clamp-1">{productoTitulo}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">¿Cuál es el problema? *</label>
              <div className="space-y-2">
                {RAZONES.map((r) => (
                  <label key={r} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${razon === r ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input
                      type="radio"
                      name="razon"
                      value={r}
                      checked={razon === r}
                      onChange={() => { setRazon(r); setError(''); }}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{r}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Detalles adicionales (opcional)</label>
              <textarea
                value={descripcion}
                onChange={(e) => { if (e.target.value.length <= 500) setDescripcion(e.target.value); }}
                placeholder="Describe el problema con más detalle..."
                rows={3}
                className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-red-400 resize-none"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{descripcion.length}/500</p>
            </div>

            {error && (
              <p className="text-red-600 text-sm mb-3 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>{error}
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl font-medium text-sm cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={enviando}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {enviando ? (
                  <><i className="ri-loader-4-line animate-spin mr-2"></i>Enviando...</>
                ) : (
                  <><i className="ri-flag-line mr-2"></i>Enviar Reporte</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
